import { Component, DoCheck, Input, inject } from '@angular/core';
import { AufnahmePunktDocument, Baumart, bodenformString, isB, isG, isM, roundToNumber } from '../../../data-model';
import { EvaluationService } from '../../evaluation.service';
import { ValidationService } from '../../validation.service';
import { DataService } from 'src/app/data.service';
import { AuthService } from 'src/app/auth.service';
import { ProjectService } from 'src/app/project.service';
import { ConfirmService } from '@luzula/luzula-lib';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-allgemein',
    templateUrl: './bohrpunkt-allgemein.component.html',
    styleUrls: ['../hl-table.component.css'],

})
export class BohrpunktAllgemeinComponent implements DoCheck {

  @Input() bp!: AufnahmePunktDocument;
  @Input() printView!: boolean;

  roundToNumber = roundToNumber;
  aus = inject(AuthService);

  private ps = inject(ProjectService);
  private confirmService = inject(ConfirmService);

  constructor(
    private ds: DataService,
    private es: EvaluationService,
    private vs: ValidationService,
  ) { }

  /** Prüft ob Kartierer/Datum angezeigt werden darf (nicht für Gäste und nicht-angemeldete) */
  canSeePersonalData(): boolean {
    return this.aus.isAuthenticated.getValue() && !this.aus.amI('gast');
  }

  ngDoCheck(): void {
    this.es.setContext(this.bp);
    this.vs.setContext(this.bp);
  }

  baumarten(): string {
    if (typeof this.bp.extendedHeader.baumarten === 'string') return this.bp.extendedHeader.baumarten;

    const baumarten = this.bp.extendedHeader.baumarten as Baumart[];
    let bas = '';
    for (const ba of baumarten) bas += ba.art + '';

    return bas;
  }

  adresse(): string {
    const a = this.bp.header.adresse;
    return '${a.abteilung}${a.unterabteilung}${a.teilflaeche}';
  }

  hasGemarkung(): boolean {
    return !!this.bp.header.adresse.gemarkung || !!this.bp.header.adresse.flur || !!this.bp.header.adresse.flurstueck;
  }

  isB(): boolean {
    return isB(this.bp);
  }

  isG(): boolean {
    return isG(this.bp);
  }

  isM(): boolean {
    return isM(this.bp);
  }

  bodenformString(which: string) {
		return bodenformString(this.bp.header.bodenform, which);
  }

  qualityString(quality: number) {
    const e = this.ds.hardData.Qualitätsstufen.find((entry: any) => entry.id === quality);
    return e ? e.name : "unbekannt";
  }

  hauptbodenform = () => this.bodenformString('hbf');
  feinbodenform = () => this.bodenformString('fbf');

  /**
   * Gibt die zugewiesenen Projekte als formatierten String zurück.
   * Format: "Name (Datum), Name (Datum), ..."
   */
  projekteString(): string {
    const projects = this.bp.header.projects;
    if (!projects || projects.length === 0) return 'Kein Projekt zugeordnet';

    return projects.map(p => {
      const dateStr = this.parseProjectDate(p.id);
      return dateStr ? `${p.name} (${dateStr})` : p.name;
    }).join(', ');
  }

  get canRemoveProject(): boolean {
    return this.ps.canAssignToProject();
  }

  async removeProject(projectId: string, projectName: string): Promise<void> {
    const confirmed = await this.confirmService.confirm({
      title: 'Projektzuordnung entfernen',
      message: `Zuordnung zu "${projectName}" wirklich entfernen?`,
      confirmText: 'Entfernen',
      cancelText: 'Abbrechen',
      confirmClass: 'btn-danger'
    });
    if (!confirmed) return;
    if (!this.bp.header.projects) return;
    this.bp.header.projects = this.bp.header.projects.filter(p => p.id !== projectId);
    await this.ds.saveDocument(this.bp);
  }

  /**
   * Extrahiert das Erstellungsdatum aus der Projekt-ID (Format: YYYYMMDD_...)
   */
  parseProjectDate(projectId: string): string {
    if (!projectId || projectId.length < 8) return '';

    const datepart = projectId.substring(0, 8);
    if (!/^\d{8}$/.test(datepart)) return '';

    const year = datepart.substring(0, 4);
    const month = datepart.substring(4, 6);
    const day = datepart.substring(6, 8);

    return `${day}.${month}.${year}`;
  }

  /**
   * Erstellt eine verbale Zusammenfassung der Relief-Informationen
   * z.B. "Ebene in Horstlage, doppelt konvex; leicht bewegt"
   * Zeigt auch Teilinformationen an, wenn nicht alle Felder ausgefüllt sind.
   */
  reliefBeschreibung(): string {
    const relief = this.bp?.extendedHeader?.relief;
    if (!relief) return '';

    const parts: string[] = [];

    // Teil 1: Neigung + Hangteil + Einbindung/Hanganschluss
    let neigungTeil = '';

    if (relief.neigungsstufe) {
      neigungTeil = relief.neigungsstufe;
      // Bei Hängen: Hangteil anhängen
      if (relief.hangteil) {
        // z.B. "schwächer geneigter Lehn-Oberhang"
        neigungTeil = neigungTeil.replace(/hang$/, `-${relief.hangteil}`);
      }
    } else if (relief.hangteil) {
      // Nur Hangteil ohne Neigungsstufe
      neigungTeil = relief.hangteil;
    }

    // Einbindung/Lage anhängen (auch ohne Neigungsstufe)
    if (relief.einbindungEbene) {
      if (neigungTeil) {
        neigungTeil += ` in ${relief.einbindungEbene}`;
      } else {
        neigungTeil = relief.einbindungEbene;
      }
    } else if (relief.hanganschluss) {
      let anschlussTeil = relief.hanganschluss;
      if (relief.hoehenspanne) {
        anschlussTeil += ` (${relief.hoehenspanne} m)`;
      }
      if (neigungTeil) {
        neigungTeil += ` in ${anschlussTeil}`;
      } else {
        neigungTeil = anschlussTeil;
      }
    } else if (relief.hoehenspanne && neigungTeil) {
      // Höhenspanne ohne Hanganschluss aber mit Neigung
      neigungTeil += ` (${relief.hoehenspanne} m)`;
    }

    if (neigungTeil) {
      parts.push(neigungTeil);
    }

    // Teil 2: Wölbung (verbal)
    if (relief.woelbung) {
      const woelbungMap: Record<string, string> = {
        'xx': 'doppelt konvex',
        'xg': 'konvex-gestreckt',
        'xv': 'konvex-konkav',
        'gx': 'gestreckt-konvex',
        'gg': 'doppelt gestreckt',
        'gv': 'gestreckt-konkav',
        'vx': 'konkav-konvex',
        'vg': 'konkav-gestreckt',
        'vv': 'doppelt konkav'
      };
      const woelbungText = woelbungMap[relief.woelbung] || relief.woelbung;
      parts.push(woelbungText);
    }

    // Teil 3: Hangrichtung
    if (relief.hangrichtung) {
      parts.push(`${relief.hangrichtung}-gerichtet`);
    }

    // Erster Teil zusammenfügen (Komma-getrennt)
    let result = parts.join(', ');

    // Teil 4: Innere Rauhheit (nach Semikolon)
    if (relief.innereRauhheit) {
      if (result) {
        result += '; ' + relief.innereRauhheit;
      } else {
        result = relief.innereRauhheit;
      }
    }

    return result;
  }

}
